import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { startKAccuracyAssessment } from '../../config/api';
import { TIMER_HIDDEN_KEY } from '../PreKAssessment/shared/AssessmentHeader';
import { Screen, KAccuracySettings, KAccuracyResults } from './types';
import { Question } from '../../types';
import SelectionScreen from './SelectionScreen';
import QuestionScreen from './QuestionScreen';
import ResultScreen from './ResultScreen';
import { logError } from '../../utils/errorReporting';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

const KAccuracyAssessment: React.FC = () => {
  const { user } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('selection');
  const [settings, setSettings] = useState<KAccuracySettings | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [results, setResults] = useState<KAccuracyResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Clear K assessment timer-hidden preference when student reaches accuracy assessment
  useEffect(() => {
    localStorage.removeItem(TIMER_HIDDEN_KEY);
  }, []);

  const handleStart = async () => {
    if (!user?.userId) return;

    try {
      setIsLoading(true);
      const response = await startKAccuracyAssessment(user.userId);

      setSettings({
        questions: response.questions,
        assessmentId: response.assessmentId
      });
      setQuestions(response.questions);
      setCurrentScreen('question');
    } catch (error) {
      logError(error, {
        component: 'KAccuracyAssessment',
        action: 'handleStart',
        userId: user?.userId
      });
      alert('Failed to start assessment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnd = (assessmentResults: KAccuracyResults) => {
    setResults(assessmentResults);
    setCurrentScreen('result');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Please log in to continue.</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {user?.role === 'admin' && (
        <div className="absolute top-4 right-4 z-50">
          <UserProfileDropdown />
        </div>
      )}
      {currentScreen === 'selection' && (
        <SelectionScreen onStart={handleStart} isLoading={isLoading} />
      )}
      {currentScreen === 'question' && settings && (
        <QuestionScreen
          onEnd={handleEnd}
          settings={settings}
          questions={questions}
        />
      )}
      {currentScreen === 'result' && results && (
        <ResultScreen results={results} />
      )}
    </div>
  );
};

export default KAccuracyAssessment;
